/**
 * Shared selection-ring styling for the Map's selectable glyph layers
 * (REQ-UI-051; ADR-024 §5; storyboard Flow 02 + Flow Q §"Frame 2").
 *
 * The selected entity is highlighted by a dedicated stroke-only "ring"
 * circle layer drawn over the glyph/marker layer, ``setFilter``-ed to the
 * selected feature id. The detection-glyph layer
 * (:file:`detection-layer-impl`), the alert-glyph layer
 * (:file:`alert-layer-impl`), and the platform-marker layer
 * (:file:`snapshot-layer-impl`) use the same ring so a selection reads
 * identically across entity kinds — and it matches the list panels' selection
 * treatment (the ``bg-brand`` left-edge stripe).
 *
 * The ring is the **action accent** (the ``--brand`` token — amber in dark,
 * blue in light), per storyboard Flow Q §"Frame 2"/§"Frame 5" ("action-color
 * selection ring"). To keep the dark amber ring unambiguous against the
 * status markers, the ``degraded`` status colour was moved off amber to a
 * deep orange (globals.css ``--status-degraded`` + STATUS_FALLBACK_HEX_MAP)
 * — REQ-UI-046.
 *
 * Ring width / opacity / offset are the one-line tunables for the browser
 * verification pass; keeping them here means a tweak applies to every entity
 * kind at once.
 */
import type { ExpressionSpecification } from "maplibre-gl";

/**
 * Fallback ring colour (dark ``--brand`` amber) for jsdom / SSR where
 * ``getComputedStyle`` can't resolve the token — dark-first, matching the
 * STATUS_FALLBACK_HEX_MAP convention.
 */
export const SELECTION_RING_FALLBACK_HEX = "#f5a623";

/**
 * The active selection-ring colour, resolved from the ``--brand`` action
 * token at paint time so the ring tracks the theme (amber in dark, blue in
 * light) — mirroring how the status markers resolve ``--status-*`` via
 * :func:`marker-styling.getStatusColor`. ``--brand`` is stored as bare HSL
 * channels (``37 91% 55%``) for ``hsl(var(--brand))`` use in CSS, so the
 * resolved channels are wrapped in ``hsl(...)`` to hand MapLibre a valid
 * colour (the wrap :func:`snapshot-layer-impl.resolveCssVarColor` also does).
 */
export function getSelectionRingColor(): string {
  if (typeof document === "undefined") {
    return SELECTION_RING_FALLBACK_HEX;
  }
  const resolved = getComputedStyle(document.documentElement).getPropertyValue("--brand").trim();
  return resolved === "" ? SELECTION_RING_FALLBACK_HEX : `hsl(${resolved})`;
}

/** Selection-ring stroke width (px). */
export const SELECTION_RING_STROKE_WIDTH = 2.5;

/** Selection-ring stroke opacity. */
export const SELECTION_RING_STROKE_OPACITY = 0.95;

/** Px added to the glyph/marker radius so the ring sits just outside it. */
export const SELECTION_RING_RADIUS_OFFSET_PX = 5;

/**
 * A ``circle-radius`` interpolation expression that tracks a base layer's
 * per-zoom radius stops plus :data:`SELECTION_RING_RADIUS_OFFSET_PX`, so the
 * ring hugs the glyph at every zoom.
 */
export function selectionRingRadiusExpression(
  baseStops: ReadonlyArray<readonly [number, number]>,
): ExpressionSpecification {
  return [
    "interpolate",
    ["linear"],
    ["zoom"],
    ...baseStops.flatMap(([zoom, radius]) => [zoom, radius + SELECTION_RING_RADIUS_OFFSET_PX]),
  ];
}
